import {
  ChangeDetectionStrategy,
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  ViewChild
} from "@angular/core";
import { NonNullableFormBuilder, Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { BehaviorSubject, Subscription } from "rxjs";
import { take } from "rxjs/operators";
import { AddressSummaryService } from "@onsip/common/services/api/resources/addressSummary/address-summary.service";
import { AppAddressService } from "@onsip/common/services/api/resources/appAddress/app-address.service";
import { VoicemailService } from "@onsip/common/services/api/resources/voicemail/voicemail.service";
import { SnackbarService } from "../../../../../shared/components/snackbar/snackbar.service";
import { AdvVoicemailboxWithExt, VoicemailAdvancedInfo } from "../../../admin-voicemail-types";
import { ModalMaterialComponent } from "../../../../../shared/components/modal/modal-material.component";
import { ExtensionsListComponent } from "@onsip/web/features/shared/components/extensionsList/extensions-list.component";
import { SipAddressFieldComponent } from "@onsip/web/features/shared/components/sipAddressField/sip-address-field.component";
import {
  convertVmTransferAddress,
  NONE_TRANSFER_CUSTOM_OPTIONS,
  NONE_TRANSFER_OPTION
} from "../../../config/none-option.helper";
import { E2eLocators } from "@onsip/e2e/configs/locators";

@Component({
  selector: "onsip-admin-voicemail-advanced-edit",
  templateUrl: "./admin-voicemail-advanced-edit.component.html",
  styleUrls: ["./admin-voicemail-advanced-edit.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AdminVoicemailAdvancedEditComponent implements OnInit, OnDestroy {
  @Input() voicemail!: AdvVoicemailboxWithExt;
  @Output() viewPanel = new EventEmitter<void>();
  @ViewChild("sipAddressField") sipAddressField!: SipAddressFieldComponent;
  @ViewChild("extensionsList") extensionsListRef!: ExtensionsListComponent;

  customOptions = NONE_TRANSFER_CUSTOM_OPTIONS;
  E2eLocators = E2eLocators;
  formGroup = this.formbuilder.group({
    timezone: this.formbuilder.control("US/Eastern"),
    sayDateTime: this.formbuilder.control(false),
    sayDuration: this.formbuilder.control(false),
    sayCallerId: this.formbuilder.control(false),
    extensionsList: this.formbuilder.control(<Array<string>>[]),
    sipAddressUsername: this.formbuilder.control(""),
    transferAddress: this.formbuilder.control(""),
    pagerNumber: this.formbuilder.control("", { validators: Validators.email })
  });

  // copied from voicemail-edit, can be its own component, ¯\_(ツ)_/¯
  timeZones: Array<[string, string]> = [
    // example: [api value, display value]
    ["US/Eastern", "US Eastern"],
    ["US/Central", "US Central"],
    ["US/Mountain", "US Mountain"],
    ["America/Phoenix", "US Mountain - Arizona"],
    ["US/Pacific", "US Pacific"],
    ["US/Hawaii", "US Hawaii"]
  ];

  // making this an instance variable so we dont have to concatenate username and domain every time
  private currentTransferAddress = "";

  private unsubscriber = new Subscription();

  loadingStateComplete$ = new BehaviorSubject(true);

  constructor(
    private formbuilder: NonNullableFormBuilder,
    private voicemailService: VoicemailService,
    private appAddress: AppAddressService,
    private snackbar: SnackbarService,
    private translate: TranslateService,
    private addressSummaryService: AddressSummaryService,
    private dialog: MatDialog
  ) {}

  ngOnInit() {
    this.currentTransferAddress = this.voicemail.transferAddress
      ? this.voicemail.transferAddress.username + "@" + this.voicemail.transferAddress.domain
      : "";

    this.formGroup.patchValue({
      timezone: this.voicemail.timezone,
      sayDateTime: this.voicemail.sayDateTime,
      sayCallerId: this.voicemail.sayCallerId,
      sayDuration: this.voicemail.sayDuration,
      sipAddressUsername: this.voicemail.voicemailboxAddress?.username,
      transferAddress: this.currentTransferAddress || NONE_TRANSFER_OPTION,
      pagerNumber: this.voicemail.pager,
      extensionsList: this.voicemail.extensions
    });
  }

  ngOnDestroy() {
    this.unsubscriber.unsubscribe();
  }

  togglePanel(): void {
    this.unsubscriber.add(
      this.dialog
        .open(ModalMaterialComponent, {
          panelClass: ["mat-typography", "onsip-dialog-universal-style"],
          data: {
            title: this.translate.instant("ONSIP_I18N.CANCEL_EDITS"),
            message: this.translate.instant(
              "ONSIP_I18N.ANY_CHANGES_YOU_HAVE_MADE_WILL_NOT_BE_SAVED"
            ),
            primaryBtnText: this.translate.instant("ONSIP_I18N.OK"),
            secondaryBtnText: this.translate.instant("ONSIP_I18N.KEEP_EDITING"),
            primaryBtnFlat: true
          }
        })
        .afterClosed()
        .pipe(take(1))
        .subscribe(res => {
          if (res && res.doPrimaryAction) this.viewPanel.next();
        })
    );
  }

  onSubmit(): void {
    if (this.formGroup.valid) {
      const promises = [];
      const voicemailAdvancedInfo: VoicemailAdvancedInfo = this.formGroup.getRawValue();
      // only make api call if voicemail form field values have changed
      if (this.hasUpdatedVoicemailParams(voicemailAdvancedInfo)) {
        this.loadingStateComplete$.next(false);
        const params = {
          Timezone: voicemailAdvancedInfo.timezone,
          SayCallerId: voicemailAdvancedInfo.sayCallerId,
          SayDateTime: voicemailAdvancedInfo.sayDateTime,
          SayDuration: voicemailAdvancedInfo.sayDuration,
          TransferAddressUsername: convertVmTransferAddress(voicemailAdvancedInfo.transferAddress),
          Pager: voicemailAdvancedInfo.pagerNumber,
          // not modified by the advanced form but still required for api respoonse
          AdvVoicemailboxId: this.voicemail.advVoicemailboxId,
          Name: this.voicemail.name,
          Mailbox: this.voicemail.mailbox,
          Password: this.voicemail.password,
          Email: this.voicemail.email,
          "EmailOptions[attach]": this.voicemail.emailOptions.attach,
          Mwi: this.voicemail.mwi
        };

        promises.push(this.voicemailService.advVoicemailboxEdit(params));
      }

      // editing sip address of voicemailbox is handled by a different api
      const res = this.editAppSipAddress(voicemailAdvancedInfo);
      if (res) {
        promises.push(res);
      }

      Promise.all(promises)
        .then(res => {
          if (res.every(item => item?.status !== "error")) {
            this.handleSuccess();
            this.extensionsListRef.editExtensions(
              this.voicemail.voicemailboxAddress?.username || ""
            );
          } else {
            const error = res.find(item => item?.status === "error" && item.data.message)?.data
              .message as string;
            this.handleError(error);
          }
        })
        .catch(() => {
          this.handleError();
        });
    }
  }

  trackByItemId(id: number, item: { id: string }): string {
    return item.id;
  }

  private hasUpdatedVoicemailParams(formGroupValue: VoicemailAdvancedInfo): boolean {
    return (
      formGroupValue.timezone !== this.voicemail.timezone ||
      formGroupValue.sayDuration !== this.voicemail.sayDuration ||
      formGroupValue.sayDateTime !== this.voicemail.sayDateTime ||
      formGroupValue.sayCallerId !== this.voicemail.sayCallerId ||
      formGroupValue.transferAddress !== this.currentTransferAddress ||
      formGroupValue.pagerNumber !== this.voicemail.pager
    );
  }

  private editAppSipAddress(formGroupValue: VoicemailAdvancedInfo) {
    if (this.voicemail.voicemailboxAddress) {
      // only make api call if username is different
      if (this.voicemail.voicemailboxAddress.username !== formGroupValue.sipAddressUsername) {
        return this.sipAddressField.updateAppAddress(this.voicemail.voicemailboxAddress.aor);
      }
    }
  }

  private handleError(error?: string) {
    error =
      error ||
      (this.translate.instant("ONSIP_I18N.THERE_WAS_AN_ERROR_SAVING_YOUR_INFORMATION") as string);
    this.snackbar.openSnackBar(error, "error");
    this.clearErrors();
    this.loadingStateComplete$.next(true);
  }

  private handleSuccess() {
    this.snackbar.openSnackBar(
      this.translate.instant("ONSIP_I18N.YOUR_CHANGES_HAVE_BEEN_SAVED"),
      "success"
    );
    this.viewPanel.emit();
    this.loadingStateComplete$.next(true);
  }

  private clearErrors() {
    this.voicemailService.clearErrors();
    this.appAddress.clearErrors();
    this.addressSummaryService.clearErrors();
  }
}
